// Estilos da tela de Créditos SMS (/creditos-sms) e do card de pacotes.
// Mockup: docs/design/aeros-dark/mockups/Tela Aeros Dark - Créditos SMS.dc.html
import { makeStyles, alpha } from "@material-ui/core/styles";
import { AEROS, stateText } from "../../styles/aerosTokens";

// Colunas da tabela de recargas, na ordem do mockup.
export const RECHARGES_GRID =
  "150px minmax(0,1.7fr) 100px 110px 124px";

const useCreditosSmsPageStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";

  return {
    headerSubtitle: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      marginTop: 2,
    },

    // ---- faixa de saldo + KPIs ----
    topGrid: {
      display: "grid",
      gridTemplateColumns:
        "minmax(0,1.15fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1fr)",
      gap: 14,
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "repeat(2, minmax(0,1fr))",
      },
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    saldoCard: {
      background: `linear-gradient(135deg, ${alpha(
        AEROS.defaultPrimary,
        0.16
      )}, ${alpha(AEROS.cyan, 0.12)})`,
      border: `1px solid ${alpha(AEROS.defaultPrimary, 0.35)}`,
      borderRadius: 14,
      padding: "18px 20px",
      display: "flex",
      flexDirection: "column",
      gap: 4,
      minWidth: 0,
    },
    saldoLabel: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: stateText(theme, "#C08BFF", "purple"),
    },
    saldoRow: { display: "flex", alignItems: "baseline", gap: 8 },
    saldoValue: {
      fontFamily: AEROS.fontDisplay,
      fontSize: 34,
      fontWeight: 700,
      letterSpacing: "-0.03em",
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.primary,
      lineHeight: 1.1,
    },
    saldoUnit: {
      fontSize: 13,
      color: theme.palette.text.secondary,
      fontWeight: 600,
    },
    saldoHint: { fontSize: 11.5, color: theme.palette.text.primary },

    kpiCard: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      borderRadius: 14,
      padding: "16px 18px",
      minWidth: 0,
    },
    kpiLabel: {
      fontSize: 11,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
    },
    kpiValue: {
      fontFamily: AEROS.fontDisplay,
      fontSize: 24,
      fontWeight: 700,
      letterSpacing: "-0.02em",
      marginTop: 4,
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    kpiHint: {
      fontSize: 11.5,
      color: theme.palette.aeros.textTertiary,
      marginTop: 2,
    },
    kpiSuccess: { color: stateText(theme, AEROS.success, "success") },
    kpiInfo: { color: stateText(theme, AEROS.cyan, "info") },

    // ---- aviso de saldo baixo ----
    alerta: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      backgroundColor: alpha(AEROS.warning, 0.07),
      border: `1px solid ${alpha(AEROS.warning, 0.28)}`,
      borderRadius: 12,
      padding: "12px 16px",
      flexWrap: "wrap",
      "& > .MuiSvgIcon-root": {
        fontSize: 19,
        color: stateText(theme, AEROS.warning, "warning"),
        flex: "none",
      },
    },
    alertaTexto: {
      flex: 1,
      fontSize: 12.5,
      color: theme.palette.text.primary,
      minWidth: 200,
    },
    alertaBotao: {
      border: `1px solid ${alpha(AEROS.warning, 0.4)}`,
      color: stateText(theme, AEROS.warning, "warning"),
      borderRadius: 9,
      padding: "5px 12px",
      fontSize: 12,
      fontWeight: 600,
      textTransform: "none",
      flex: "none",
    },

    // ---- corpo: recargas + laterais ----
    bodyGrid: {
      display: "grid",
      gridTemplateColumns: "minmax(0,1.55fr) minmax(0,1fr)",
      gap: 16,
      alignItems: "start",
      [theme.breakpoints.down("md")]: { gridTemplateColumns: "1fr" },
    },
    card: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      borderRadius: 14,
      minWidth: 0,
    },
    cardPad: { padding: "16px 18px" },
    cardHead: {
      display: "flex",
      alignItems: "center",
      padding: "14px 18px 12px",
      gap: 10,
      borderBottom: `1px solid ${border}`,
      flexWrap: "wrap",
    },
    cardTitle: {
      fontSize: 13,
      fontWeight: 700,
      color: theme.palette.text.primary,
    },
    cardSub: { fontSize: 11.5, color: theme.palette.text.secondary },

    // pílulas de filtro de status
    filtros: { marginLeft: "auto", display: "flex", gap: 6, flexWrap: "wrap" },
    filtro: {
      fontSize: 11.5,
      fontWeight: 600,
      borderRadius: 99,
      padding: "2px 10px",
      minWidth: "auto",
      textTransform: "none",
      color: theme.palette.text.secondary,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.12)"}`,
      lineHeight: 1.6,
    },
    filtroAtivo: {
      color: theme.palette.text.primary,
      backgroundColor: alpha(AEROS.defaultPrimary, 0.18),
      borderColor: alpha(AEROS.defaultPrimary, 0.4),
    },

    rowHead: {
      display: "grid",
      gridTemplateColumns: RECHARGES_GRID,
      gap: 10,
      padding: "9px 18px",
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
      borderBottom: `1px solid ${border}`,
    },
    row: {
      display: "grid",
      gridTemplateColumns: RECHARGES_GRID,
      gap: 10,
      padding: "11px 18px",
      borderBottom: `1px solid ${
        isLight ? theme.palette.divider : "rgba(255,255,255,0.05)"
      }`,
      alignItems: "center",
      fontSize: 13,
    },
    cellDate: {
      color: theme.palette.text.secondary,
      fontVariantNumeric: "tabular-nums",
    },
    cellPack: { display: "flex", alignItems: "center", gap: 8, minWidth: 0 },
    packIcon: {
      fontSize: 16,
      color: stateText(theme, "#C08BFF", "purple"),
      flex: "none",
    },
    packName: {
      fontWeight: 600,
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    num: {
      textAlign: "right",
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.primary,
    },
    numMuted: {
      textAlign: "right",
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.secondary,
    },
    cellStatus: { textAlign: "right" },
    pill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: 11,
      fontWeight: 600,
      borderRadius: 99,
      padding: "3px 10px",
      whiteSpace: "nowrap",
    },
    pillDot: { width: 6, height: 6, borderRadius: "50%", flex: "none" },
    rodape: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "12px 18px",
      fontSize: 12,
      color: theme.palette.aeros.textTertiary,
      "& .MuiSvgIcon-root": { fontSize: 15, flex: "none" },
    },
    vazio: {
      padding: "24px 18px",
      textAlign: "center",
      fontSize: 12.5,
      color: theme.palette.aeros.textTertiary,
    },

    // ---- coluna lateral ----
    lateral: { display: "flex", flexDirection: "column", gap: 16, minWidth: 0 },
    grafico: {
      display: "flex",
      alignItems: "flex-end",
      gap: 8,
      height: 110,
      marginTop: 12,
    },
    graficoCol: {
      flex: 1,
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-end",
      alignItems: "center",
      gap: 6,
      minWidth: 0,
    },
    // A barra tem altura em %, então o trilho precisa de altura PRÓPRIA:
    // dentro de uma coluna que se ajusta ao conteúdo, "height: 60%" não
    // resolve para nada e a barra some.
    graficoTrilho: {
      width: "100%",
      height: 70,
      display: "flex",
      alignItems: "flex-end",
    },
    graficoValor: {
      fontSize: 10.5,
      color: theme.palette.text.secondary,
      fontVariantNumeric: "tabular-nums",
    },
    // minHeight 2: um dia sem consumo ainda precisa aparecer no eixo
    graficoBarra: {
      width: "100%",
      borderRadius: "6px 6px 3px 3px",
      backgroundColor: alpha(AEROS.defaultPrimary, 0.35),
      minHeight: 2,
    },
    graficoBarraPico: {
      background: `linear-gradient(180deg, ${AEROS.defaultPrimary}, ${AEROS.cyan})`,
    },
    graficoDia: { fontSize: 10.5, color: theme.palette.aeros.textTertiary },

    origemLinha: { marginTop: 10 },
    origemRow: { display: "flex", alignItems: "baseline", fontSize: 12.5 },
    origemLabel: { color: theme.palette.text.primary },
    origemValor: {
      marginLeft: "auto",
      fontWeight: 600,
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.primary,
    },
    track: {
      height: 6,
      borderRadius: 99,
      backgroundColor: isLight ? "rgba(15,20,40,0.07)" : "rgba(255,255,255,0.07)",
      marginTop: 5,
      overflow: "hidden",
    },
    trackFill: { height: "100%", borderRadius: 99 },
    nota: {
      fontSize: 11.5,
      color: theme.palette.aeros.textTertiary,
      marginTop: 10,
    },
  };
});

export default useCreditosSmsPageStyles;
